import { GridApi, IRowNode } from "@ag-grid-community/core";
import { inject, Injectable } from "@angular/core";
import {
  AccountMarketplace,
  AccountMarketplaceDspAdvertiserId,
  AccountState,
  AccountType,
  Currency,
} from "@front/m19-api-client";
import {
  ACOS,
  AD_CONVERSIONS,
  AD_SALES,
  CLICK_THROUGH_RATE,
  CLICKS,
  CONVERSION_RATE,
  COST,
  CPC,
  IMPRESSIONS,
  Metric,
  MetricRegistry,
  ROAS,
  TACOS,
  TOTAL_ORDERS,
  TOTAL_ROAS,
  TOTAL_SALES,
  TOTAL_TACOS,
} from "@front/m19-metrics";
import { AdStatsEx } from "@front/m19-models";
import { StatDisplayMode } from "@front/m19-models/dataset";
import {
  AccountSelectionService,
  AdStatsType,
  arrayGroupBy,
  OrganizationAccountGroupService,
  StatsService,
  StatsTypeService,
  UserSelectionService,
} from "@front/m19-services";
import { mergeSeveralDates } from "@front/m19-utils";
import { SPONSORED_SALES_SHARE } from "@m19-board/models/MetricsDef";
import {
  constantField,
  CsvExportService,
  CsvField,
  fieldExtractor,
  metricField,
  simpleField,
} from "@m19-board/services/csv-export.service";
import { ChartData } from "@m19-board/shared/chart-modal/chart-modal.component";
import { AdType } from "@m19-board/shared/compact-filter/compact-filter.component";
import {
  BehaviorSubject,
  combineLatest,
  forkJoin,
  map,
  Observable,
  of,
  shareReplay,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from "rxjs";

export type StatsInfo = {
  rowId: string;
  organizationId: number;
  organizationName: string;
  accountGroupName?: string;
  accountName?: string;
  accountType?: AccountType;
  state?: AccountState;
  dailyStats?: AdStatsEx[];
  previousDailyStats?: AdStatsEx[];
};

export type HomeGridStats = AdStatsEx & StatsInfo;

@Injectable()
export class HomeService {
  private readonly accountSelectionService = inject(AccountSelectionService);
  private readonly statsService = inject(StatsService);
  private readonly userSelectionService = inject(UserSelectionService);
  private readonly csvExportService = inject(CsvExportService);
  private readonly organizationAccountGroupService = inject(OrganizationAccountGroupService);
  private readonly statsTypeService = inject(StatsTypeService);

  public readonly HOME_PAGE_METRICS = [
    TOTAL_SALES,
    TOTAL_ORDERS,
    AD_SALES,
    AD_CONVERSIONS,
    COST,
    ACOS,
    CLICKS,
    IMPRESSIONS,
    CLICK_THROUGH_RATE,
    CONVERSION_RATE,
    CPC,
    ROAS,
    SPONSORED_SALES_SHARE,
  ];
  public readonly CHARTS_METRICS = [...this.HOME_PAGE_METRICS, TOTAL_TACOS, TOTAL_ROAS];
  public readonly GRID_METRICS = [...this.HOME_PAGE_METRICS, TACOS];

  private readonly selectedAdTypes = new BehaviorSubject<AdType[]>([AdType.SponsoredAds, AdType.DSP]);

  private readonly loadingSubject = new BehaviorSubject<boolean>(true);
  public readonly loading$ = this.loadingSubject.asObservable();

  public readonly allAccountMarketplaces$ = this.organizationAccountGroupService.allOrganizationAccountGroups$.pipe(
    map((organizations) => {
      return organizations.flatMap((organization) =>
        organization.accountGroups.flatMap((accountGroup) =>
          accountGroup
            .getAccountMarketplaces()
            .filter(
              (accountMarketplace) =>
                accountMarketplace.hasAccessToAdvertising &&
                (accountMarketplace.state == AccountState.BIDDER_ON ||
                  accountMarketplace.state == AccountState.DOWNLOADER_ON),
            )
            .map((accountMarketplace) => this.getAccountMarketplace(accountMarketplace, organization.organizationName)),
        ),
      );
    }),
    shareReplay(1),
  );

  public readonly selectedAccountMarketplacesMap$ = this.accountSelectionService.multiAccountSelection$.pipe(
    withLatestFrom(this.allAccountMarketplaces$),
    map(([selection, allAccountMarketplaces]) => {
      const keys = new Map<string, AccountMarketplace & { organizationName?: string }>(
        selection
          .flatMap((x) => x.accountMarketplaces)
          .map((account) => {
            const orgName = allAccountMarketplaces.find((am) => am.accountId === account.accountId)?.organizationName;
            return [this.getRowKey(account), this.getAccountMarketplace(account, orgName)];
          }),
      );
      return keys;
    }),
  );

  public readonly selectedAccountMarketplaces$ = this.selectedAccountMarketplacesMap$.pipe(
    map((map) => Array.from(map.values())),
  );

  private selectedAdTypes$ = this.selectedAdTypes.pipe(
    map((adTypes) => {
      if (adTypes.length === 0) {
        return [AdType.SponsoredAds, AdType.DSP];
      }
      return adTypes;
    }),
  );

  public readonly selectedAccountMarketplacesStats$ = combineLatest([
    this.selectedAccountMarketplaces$,
    this.userSelectionService.dateRange$,
    this.selectedAdTypes$,
    this.userSelectionService.selectedCurrency$,
  ]).pipe(
    tap(() => this.loadingSubject.next(true)),
    switchMap(([accountMarketplaces, dateRange, adTypes, currency]) => {
      return this.fetchStats(dateRange, accountMarketplaces, adTypes, currency);
    }),
    tap({
      next: () => this.loadingSubject.next(false),
      error: () => this.loadingSubject.next(false),
    }),
    shareReplay(1),
  );

  public readonly selectedAccountMarketplacesPreviousStats$ = combineLatest([
    this.selectedAccountMarketplaces$,
    this.userSelectionService.periodComparison$,
    this.selectedAdTypes$,
    this.userSelectionService.selectedCurrency$,
  ]).pipe(
    switchMap(([accountMarketplaces, periodComparison, adTypes, currency]) => {
      if (!periodComparison?.period) {
        return of([]);
      }
      return this.fetchStats(periodComparison.period, accountMarketplaces, adTypes, currency);
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  public readonly previousStatsPerAccountMarketplace$ = this.selectedAccountMarketplacesPreviousStats$.pipe(
    map((stats) => {
      return arrayGroupBy(stats, (s) => this.getRowKey(s));
    }),
  );

  public readonly gridData$: Observable<HomeGridStats[]> = combineLatest([
    this.selectedAccountMarketplacesStats$,
    this.selectedAccountMarketplacesMap$,
    this.previousStatsPerAccountMarketplace$,
  ]).pipe(
    map(([stats, accounts, previousStats]) => {
      const gridData: HomeGridStats[] = [];

      for (const account of accounts.values()) {
        const accountStats = stats.filter(
          (s) => s.accountId === account.accountId && s.marketplace === account.marketplace,
        );
        const previousAccountStats = previousStats.get(this.getRowKey(account)) ?? [];
        const mergedStats = accountStats.reduce((acc, curr) => mergeSeveralDates(acc, curr), {});

        const toHomeGridStats: HomeGridStats = {
          ...mergedStats,
          ...account,
          rowId: this.getRowKey(account),
          organizationId: account.resourceOrganizationId!,
          organizationName: account.organizationName ?? "",
          state: account.state,
          accountType: account.accountType,
          dailyStats: accountStats,
          previousDailyStats: previousAccountStats,
        };
        gridData.push(toHomeGridStats);
      }
      return gridData;
    }),
  );

  public fetchStats(
    dateRange: string[],
    accountMarketplaces: AccountMarketplace[],
    selectedAdTypes: AdType[],
    currency: Currency,
  ): Observable<AdStatsEx[]> {
    if (accountMarketplaces.length === 0) {
      return of([]);
    }
    const hasOneSeller = accountMarketplaces.some((am) => am.accountType == AccountType.SELLER);
    const hasOneVendor = accountMarketplaces.some((am) => am.accountType == AccountType.VENDOR);
    const advertisers: AccountMarketplaceDspAdvertiserId[] = accountMarketplaces
      .filter((a) => a.dspAdvertiserId)
      .map((a) => ({
        accountId: a.accountId,
        marketplace: a.marketplace,
        dspAdvertiserId: a.dspAdvertiserId!,
      }));

    const getAdStats = selectedAdTypes.includes(AdType.SponsoredAds);
    const getDspStats = selectedAdTypes.includes(AdType.DSP);

    const adStats$ = getAdStats
      ? this.statsService.getAdStatsPerAccountMarketplace(accountMarketplaces, dateRange[0], dateRange[1], currency)
      : of([]);
    const salesPerSeller$ = hasOneSeller
      ? this.statsService.getSalesPerSellerAccountMarketplace(accountMarketplaces, dateRange[0], dateRange[1], currency)
      : of([]);
    const salesPerVendor$ = hasOneVendor
      ? this.statsService.getSalesPerVendorAccountMarketplace(accountMarketplaces, dateRange[0], dateRange[1], currency)
      : of([]);
    const dspStats$ =
      advertisers && getDspStats
        ? this.statsService.getDspPerAdvertiser(advertisers, dateRange[0], dateRange[1], currency)
        : of([]);

    return forkJoin([adStats$, salesPerSeller$, salesPerVendor$, dspStats$]).pipe(
      map(([adsStats, allSales, vendorSales, dspStats]) => [...adsStats, ...dspStats, ...allSales, ...vendorSales]),
    );
  }

  public getRowKey<T extends { accountId?: string; marketplace?: string }>(account: T): string {
    return `${account.accountId}_${account.marketplace}`;
  }

  public getRowChartData(rowsToProcess: IRowNode<HomeGridStats>[]): Observable<ChartData<AdStatsEx>> {
    return this.gridData$.pipe(
      map((_) => {
        const rowsData = rowsToProcess.map((row) => row.data).filter((data) => !!data);
        return {
          data: rowsData.flatMap((row) => row.dailyStats ?? []),
          totalData: this.mergeStats(rowsData.flatMap((row) => row.dailyStats ?? [])),
          previousData: rowsData.flatMap((row) => row.previousDailyStats ?? []),
          totalPreviousData: this.mergeStats(rowsData.flatMap((row) => row.previousDailyStats ?? [])),
        };
      }),
    );
  }

  public mergePreviousStats(rows: HomeGridStats[]): AdStatsEx | undefined {
    const previousStats = rows.flatMap((row) => row.previousDailyStats ?? []);
    if (previousStats.length === 0) return undefined;
    return this.mergeStats(previousStats);
  }

  public mergeStats(stats: AdStatsEx[]) {
    return stats.reduce((acc, curr) => {
      mergeSeveralDates(acc, curr);
      return acc;
    }, {});
  }

  public exportCsv(
    gridApi: GridApi<HomeGridStats>,
    splitMode: StatDisplayMode,
    isGlobal: boolean,
    dateRange: string[],
  ) {
    this.gridData$
      .pipe(take(1), withLatestFrom(this.userSelectionService.selectedCurrency$))
      .subscribe(([data, currency]) => {
        let exportedData: HomeGridStats[] = [];
        if (!isGlobal) {
          exportedData = data.flatMap((d) => {
            const info = this.getInfoFromGridData(d);
            const convertedStats = this.statsTypeService.convertDataToStatsType(d.dailyStats ?? [], splitMode);
            return convertedStats?.filter((ds) => ds !== undefined).map((ds) => ({ ...ds, ...info })) ?? [];
          });
        } else {
          exportedData = data.flatMap((d) => {
            const convertedStats = this.statsTypeService.convertDataToStatsType(d.dailyStats ?? [], splitMode);
            const byStatsType = arrayGroupBy(convertedStats, (ds) => ds.statsType);

            const res = [];
            for (const [type, stats] of byStatsType.entries()) {
              res.push({ ...d, ...this.mergeStats(stats), statsType: type });
            }
            return res;
          });
        }
        const sortedData = this.sortExportData(exportedData, splitMode, isGlobal);
        const fields = this.getExportFields(gridApi, splitMode, isGlobal, currency);
        this.csvExportService.exportCsv<HomeGridStats>(
          { prefix: this.getFileNamePrefix(splitMode, isGlobal), dateRange: dateRange },
          sortedData,
          fields,
        );
      });
  }

  public selectAdTypes(adTypes: AdType[]) {
    this.selectedAdTypes.next(adTypes);
  }

  private getInfoFromGridData(data: HomeGridStats): StatsInfo {
    return {
      rowId: data.rowId,
      organizationId: data.organizationId,
      organizationName: data.organizationName,
      accountName: data.accountName,
      accountGroupName: data.accountGroupName,
      accountType: data.accountType,
    };
  }

  private sortExportData(data: HomeGridStats[], splitMode: StatDisplayMode, isGlobal: boolean): HomeGridStats[] {
    return data.sort((a, b) => {
      if (a.isM19 !== b.isM19 && splitMode === StatDisplayMode.SPLIT) {
        return a.isM19 ? -1 : 1;
      }
      if (isGlobal) return 0;
      if (a.date! < b.date!) return -1;
      if (a.date! > b.date!) return 1;
      return 0;
    });
  }

  private getFileNamePrefix(splitMode: StatDisplayMode, isGlobal: boolean): string {
    return `${splitMode}_${isGlobal ? "global" : "daily"}_stats`;
  }

  private getExportFields(
    gridApi: GridApi<HomeGridStats>,
    splitMode: StatDisplayMode,
    isGlobal: boolean,
    currency: Currency,
  ): CsvField<HomeGridStats>[] {
    const fields: CsvField<HomeGridStats>[] = [
      ...(splitMode !== StatDisplayMode.SPLIT
        ? []
        : [
            fieldExtractor("Type", (d: HomeGridStats) =>
              d.isM19 || d.statsType === AdStatsType.MANAGED ? "Managed" : "Unmanaged",
            ),
          ]),
      simpleField("organizationName", "Organization"),
      simpleField("accountGroupName", "AccountGroup"),
      simpleField("accountName", "Account"),
      simpleField("marketplace", "Marketplace"),
      constantField("Currency", currency),
      ...(isGlobal ? [] : [fieldExtractor("date", (d: HomeGridStats) => d.date!.toString())]),
      ...this.getGridMetrics(gridApi).map((m) => metricField(m)),
    ];
    return fields;
  }

  private getGridMetrics(gridApi: GridApi<HomeGridStats>): Metric<HomeGridStats>[] {
    return gridApi
      ?.getAllDisplayedColumns()
      .map((c) => MetricRegistry.get(c.getColId()))
      .filter((c) => !!c) as Metric<HomeGridStats>[];
  }

  private getAccountMarketplace(
    accountMarketplace: AccountMarketplace,
    orgName?: string,
  ): AccountMarketplace & { organizationName?: string } {
    return {
      resourceOrganizationId: accountMarketplace.resourceOrganizationId,
      accountId: accountMarketplace.accountId,
      marketplace: accountMarketplace.marketplace,
      accountName: accountMarketplace.accountName,
      accountGroupId: accountMarketplace.accountGroupId,
      accountGroupName: accountMarketplace.accountGroupName,
      state: accountMarketplace.state,
      accountType: accountMarketplace.accountType,
      useSourcingMetrics: accountMarketplace.useSourcingMetrics,
      dspAdvertiserId: accountMarketplace.dspAdvertiserId,
      organizationName: orgName,
    };
  }
}
